import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Banknote, TrendingUp, TrendingDown, DollarSign } from "lucide-react";
import { Progress } from "@shared/ui/progress";
import { useOutstandingSummary } from "../../hooks";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useCurrency } from "@shared/selectors/globalSelectors";

interface PaymentPerformanceProps {
  timeRange: "day" | "week" | "month" | "quarter" | "year";
  userRole: "manager" | "executive" | "callcenter" | "recovery";
}

export function PaymentPerformance({ timeRange, userRole }: PaymentPerformanceProps) {
      const remoteUtilityId = getRemoteUtilityId();
      const currency = useCurrency();
  const { 
    data: outstandingData, 
    isLoading: isOutstandingLoading, 
    error: outstandingError 
  } = useOutstandingSummary({
    remote_utility_id: remoteUtilityId,
  });

  // Helper function to format currency
  const formatCurrency = (amount: number) => {
    if (amount >= 1000000) {
      return (amount / 1000000).toFixed(1) + "M";
    } else if (amount >= 1000) {
      return (amount / 1000).toFixed(1) + "K";
    } else {
      return amount.toLocaleString();
    }
  };

  // Transform API data to payment performance data
  const paymentData = outstandingData?.result ? {
    totalPayments: outstandingData.result.totalPaidAmount || 0,
    totalPaymentsTrend: Math.abs(outstandingData.result.paidPercentage - 50), // Assuming 50% as baseline
    onTimePercentage: outstandingData.result.onTimePaymentPercentage || 0,
    digitalAdoption: 65, // This might need another API endpoint
    outstandingAmount: outstandingData.result.totalOutstandingBalance || 0,
    outstandingTrend: outstandingData.result.outstandingPercentage , // Assuming 30% as baseline
    agingBuckets: [
      { label: "Current", value: 100 - (outstandingData.result["30"] + outstandingData.result["60"] + outstandingData.result["90"]) },
      { label: "30 Days", value: outstandingData.result["30"] },
      { label: "60 Days", value: outstandingData.result["60"] },
      { label: "90+ Days", value: outstandingData.result["90"] }
    ]
  } : {
    // Fallback data if API is not available
    totalPayments: 0,
    totalPaymentsTrend: 0,
    onTimePercentage: 0,
    digitalAdoption: 0,
    outstandingAmount: 0,
    outstandingTrend: 0,
    agingBuckets: [
      { label: "Current", value: 0 },
      { label: "30 Days", value: 0 },
      { label: "60 Days", value: 0 },
      { label: "90+ Days", value: 0 }
    ]
  };

  // Loading state
  if (isOutstandingLoading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="card-heading-text flex items-center justify-between">
            Payment Performance
            <Banknote className="h-5 w-5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-muted-foreground">Loading payment data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (outstandingError) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="card-heading-text flex items-center justify-between">
            Payment Performance
            <Banknote className="h-5 w-5 text-muted-foreground" />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-center h-32">
            <div className="text-sm text-red-500">Failed to load payment data</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="card-heading-text flex items-center justify-between">
          Payment Performance
          <Banknote className="h-5 w-5 text-muted-foreground" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="card-subheading-text text-muted-foreground">Total Payments</p>
            <div className="flex items-baseline">
              <span className="flex items-center">
              <span className="card-value-text">{currency} {formatCurrency(paymentData?.totalPayments)}</span>
              </span>
              <span className={`ml-2 text-xs flex items-center ${paymentData?.totalPaymentsTrend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                {paymentData?.totalPaymentsTrend > 0 ? <TrendingUp className="h-3 w-3 mr-0.5" /> : <TrendingDown className="h-3 w-3 mr-0.5" />}
                {paymentData?.totalPaymentsTrend}%
              </span>
            </div>
          </div>
          <div className="space-y-1">
            <p className="card-subheading-text text-muted-foreground">Outstanding Amount</p>
            <div className="flex items-baseline">
              <span className="flex items-center">
              <span className="card-value-text">{currency} {formatCurrency(paymentData.outstandingAmount)}</span>
              </span>
              <span className={`ml-2 text-xs flex items-center ${paymentData.outstandingTrend < 0 ? 'text-green-500' : 'text-red-500'}`}>
                {paymentData.outstandingTrend < 0 ? <TrendingDown className="h-3 w-3 mr-0.5" /> : <TrendingUp className="h-3 w-3 mr-0.5" />}
                {paymentData?.outstandingTrend}%
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="card-subheading-text text-muted-foreground">On-Time Payment Rate</p>
            <span className="text-sm font-medium">{paymentData.onTimePercentage.toFixed(1)}%</span>
          </div>
          <Progress value={paymentData.onTimePercentage} className="h-2" />
        </div>

        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <p className="card-subheading-text text-muted-foreground">Digital Payment Adoption</p>
            <span className="text-sm font-medium">{paymentData.digitalAdoption}%</span>
          </div>
          <Progress value={paymentData.digitalAdoption} className="h-2" />
        </div>

        <div className="space-y-1">
          <p className="card-subheading-text text-muted-foreground">Aging Breakdown</p>
          <div className="flex items-center gap-1 h-5">
            {paymentData.agingBuckets.map((bucket, index) => (
              <div 
                key={bucket.label} 
                style={{ width: `${bucket.value}%` }} 
                className={`h-full rounded ${
                  index === 0 ? 'bg-green-500' : 
                  index === 1 ? 'bg-amber-400' : 
                  index === 2 ? 'bg-orange-500' : 'bg-red-500'
                }`}
                title={`${bucket.label}: ${bucket.value}%`}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>Current</span>
            <span>30 Days</span>
            <span>60 Days</span>
            <span>90+ Days</span>
          </div>
        </div>
        
      
      </CardContent>
    </Card>
  );
}